import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

/** Legal Support mailbox named in the notice; an address, so never translated. */
export const MIGRATION_NOTICE_SUPPORT_EMAIL = 'support@tlregtech.com';

/**
 * "Migration to RegTrack 2.0" announcement shown on the sign-in (email entry) card.
 *
 * Collapsed by default to the one-line summary with an inline "Read more" control;
 * expanding reveals the coordination request, the Legal Support contact and the
 * thank-you line, followed by "Show less".
 *
 * The summary and contact sentences are single translation keys rendered through
 * `[innerHTML]` (with the bold date phrase / mailto link inside the translation) so
 * each locale keeps its own word order instead of being stitched from fragments.
 * Purely presentational: no inputs/outputs, no service calls.
 * @component
 * @selector app-migration-notice
 * @example
 *   <app-migration-notice></app-migration-notice>
 */
@Component({
  selector: 'app-migration-notice',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './migration-notice.component.html',
  styleUrl: './migration-notice.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MigrationNoticeComponent {
  /** Whether the full notice text is shown; collapsed on first render. */
  expanded = false;

  /** Translate params for the contact sentence: a mailto link to Legal Support. */
  readonly contactParams = {
    email: `<a href="mailto:${MIGRATION_NOTICE_SUPPORT_EMAIL}">${MIGRATION_NOTICE_SUPPORT_EMAIL}</a>`
  };

  /** The collapsed state's "Read more" button; absent while expanded. */
  @ViewChild('readMore') private readMore?: ElementRef<HTMLButtonElement>;

  /** The expanded text region (`#rt-mig-more`); absent while collapsed. */
  @ViewChild('more') private more?: ElementRef<HTMLElement>;

  /**
   * @param cdr - Used to render the new state synchronously, so focus can move to it.
   */
  constructor(private readonly cdr: ChangeDetectorRef) {}

  /**
   * Switches the notice between its collapsed summary and the full text.
   *
   * Each state has its own toggle button, so the one that was clicked is destroyed.
   * Focus is moved explicitly — to the revealed text on expand, back to "Read more"
   * on collapse — so keyboard and screen-reader users are not dropped to the page.
   * @returns void
   */
  toggle(): void {
    this.expanded = !this.expanded;
    this.cdr.detectChanges();
    (this.expanded ? this.more : this.readMore)?.nativeElement.focus();
  }
}
